import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Snowman — a storybook snow friend: three stacked soft blue-white snowballs,
// a carrot nose, a coal-dot smile, twig arms, a cosy red scarf and a dapper
// top hat. Built chunky and toy-like (big head, big eyes), about 1 m tall.
//
// Frame: faces +Z (where the carrot points), sits on the floor (y = 0).
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body" : the middle snowball with the twig arms, buttons and scarf.
//              Pivot at the top of the base ball so it rocks in place.
//   - "head" : the top snowball with the whole face. Pivot at the neck (where
//              the scarf sits) so nods read as a head, not a floating ball.
//   - "hat"  : the top hat. Pivot at the brim so it lifts and tips for the
//              signature hat-tip greeting.
// The base snowball stays planted (unnamed, merged into the static mesh).

const SNOW = "#e9f1f8";        // soft blue-white snow (never pure white)
const SNOW_SHADE = "#c8dbe9";  // shaded snow (underside blush)
const CARROT = "#ef8a2f";      // the carrot nose
const COAL = "#2c2f36";        // deep charcoal — eyes, smile, buttons, hat
const HAT_BAND = "#e05a4e";    // warm red hat band, matches the scarf
const SCARF = "#d94f43";       // cosy red scarf
const SCARF_CREAM = "#f6efe3"; // cream scarf stripe
const TWIG = "#8a5a33";        // twig arms

// ── frame heights ────────────────────────────────────────────────────────────
const BASE_CY = 0.26;          // base ball centre (r 0.28, sunk 0.02 into snow)
const BASE_TOP = 0.5;          // where the body ball seats
const BODY_CY = 0.6;           // body ball centre (r 0.2)
const NECK_Y = 0.76;           // scarf line — the head pivots here
const HEAD_CY = 0.89;          // head ball centre (r 0.15)
const BRIM_Y = 1.0;            // hat brim — the hat pivots here

const BODY_PIVOT: [number, number, number] = [0, BASE_TOP, 0];
const HEAD_PIVOT: [number, number, number] = [0, NECK_Y, 0];
const HAT_PIVOT: [number, number, number] = [0, BRIM_Y, 0];

// One twig arm: a main stick angled up-and-out plus a little branch fork.
function twigArm(name: string, side: number): Part[] {
  const lean = side * 0.85; // roll of the main stick away from the body
  return [
    // Main stick, rooted in the body ball, reaching up and out.
    { geometry: new THREE.CylinderGeometry(0.014, 0.018, 0.34, 6), color: TWIG,
      position: [side * 0.3, 0.72, 0], rotation: [0, 0, -lean], name },
    // A little branch forking off near the tip.
    { geometry: new THREE.CylinderGeometry(0.009, 0.011, 0.12, 5), color: TWIG,
      position: [side * 0.41, 0.83, 0.02], rotation: [0.5, 0, -lean + side * 0.7], name },
  ];
}

function snowmanParts(): Part[] {
  const parts: Part[] = [
    // ── base snowball (static) ──────────────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.28, 18, 14), color: SNOW, position: [0, BASE_CY, 0], scale: [1, 0.92, 1] },
    // A shaded skirt where the ball meets the ground.
    { geometry: new THREE.SphereGeometry(0.26, 16, 12), color: SNOW_SHADE, position: [0, 0.16, 0], scale: [1.06, 0.55, 1.06] },

    // ── body snowball ───────────────────────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.2, 18, 14), color: SNOW, position: [0, BODY_CY, 0], name: "body" },
    // Coal buttons down the front.
    { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: COAL, position: [0, 0.68, 0.185], name: "body" },
    { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: COAL, position: [0, 0.6, 0.2], name: "body" },
    ...twigArm("body", -1),
    ...twigArm("body", 1),
    // Scarf: a wrap around the neck plus a tail flapping down the front.
    { geometry: new THREE.CylinderGeometry(0.15, 0.16, 0.07, 14), color: SCARF, position: [0, NECK_Y, 0], name: "body" },
    { geometry: new THREE.CylinderGeometry(0.155, 0.158, 0.022, 14), color: SCARF_CREAM, position: [0, NECK_Y - 0.02, 0], name: "body" },
    { geometry: new THREE.BoxGeometry(0.09, 0.2, 0.035), color: SCARF, position: [0.07, 0.67, 0.15], rotation: [0.18, 0, 0.08], name: "body" },
    { geometry: new THREE.BoxGeometry(0.09, 0.04, 0.04), color: SCARF_CREAM, position: [0.075, 0.58, 0.175], rotation: [0.18, 0, 0.08], name: "body" },

    // ── head snowball + face ────────────────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.15, 18, 14), color: SNOW, position: [0, HEAD_CY, 0], name: "head" },
    // Big friendly eyes: cream whites with large coal pupils set forward.
    { geometry: new THREE.SphereGeometry(0.038, 10, 8), color: SCARF_CREAM, position: [-0.055, 0.925, 0.125], name: "head" },
    { geometry: new THREE.SphereGeometry(0.038, 10, 8), color: SCARF_CREAM, position: [0.055, 0.925, 0.125], name: "head" },
    { geometry: new THREE.SphereGeometry(0.026, 8, 6), color: COAL, position: [-0.052, 0.928, 0.155], name: "head" },
    { geometry: new THREE.SphereGeometry(0.026, 8, 6), color: COAL, position: [0.052, 0.928, 0.155], name: "head" },
    // Tiny highlights so the eyes sparkle.
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: "#fdfbf6", position: [-0.045, 0.937, 0.173], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: "#fdfbf6", position: [0.059, 0.937, 0.173], name: "head" },
    // Carrot nose pointing +Z.
    { geometry: new THREE.ConeGeometry(0.028, 0.16, 8), color: CARROT, position: [0, 0.885, 0.2], rotation: [Math.PI / 2, 0, 0], name: "head" },
    // Coal-dot smile: an upward arc of four dots.
    { geometry: new THREE.SphereGeometry(0.013, 6, 5), color: COAL, position: [-0.06, 0.85, 0.126], name: "head" },
    { geometry: new THREE.SphereGeometry(0.013, 6, 5), color: COAL, position: [-0.022, 0.832, 0.14], name: "head" },
    { geometry: new THREE.SphereGeometry(0.013, 6, 5), color: COAL, position: [0.022, 0.832, 0.14], name: "head" },
    { geometry: new THREE.SphereGeometry(0.013, 6, 5), color: COAL, position: [0.06, 0.85, 0.126], name: "head" },

    // ── top hat ─────────────────────────────────────────────────────────────
    { geometry: new THREE.CylinderGeometry(0.165, 0.165, 0.025, 16), color: COAL, position: [0, BRIM_Y, 0], name: "hat" },
    { geometry: new THREE.CylinderGeometry(0.1, 0.107, 0.17, 16), color: COAL, position: [0, BRIM_Y + 0.095, 0], name: "hat" },
    { geometry: new THREE.CylinderGeometry(0.109, 0.112, 0.045, 16), color: HAT_BAND, position: [0, BRIM_Y + 0.04, 0], name: "hat" },
  ];
  return parts;
}

export const snowman = defineModel({
  id: "snowman",
  batchId: "mess",
  name: "Snowman",
  tags: ["mess", "cute", "animated", "character", "snowman", "snow", "winter", "mascot"],
  summary: "A friendly three-ball snowman with a carrot nose, red scarf and a top hat he tips in greeting.",
  description:
    "A storybook snowman: three stacked soft blue-white snowballs with a carrot " +
    "nose, big friendly eyes, a coal-dot smile, twig arms, a cosy red scarf and " +
    "a charcoal top hat. The body, head and hat are separately posable; it ships " +
    "an idle bob and a hat-tip greeting where the hat lifts and tilts.",
  author: "models",
  footprint: 0.4,
  tiles: { w: 1, h: 1 },
  parts: snowmanParts,
  animations: [
    // 1. Idle — a slow, chilly breathing bob: body and head lift in near
    //    counter-phase, the hat rides along with a tiny lag.
    {
      id: "idle",
      label: "Idle bob",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.03, 0, 0], ease: "sine" },
            { t: 3.0, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "hat", pivot: HAT_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.022, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Hat-tip — the signature greeting: the hat lifts off, tips to the side,
    //    hovers a beat, and settles back while the head bows politely.
    {
      id: "hat-tip",
      label: "Hat-tip greeting",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "hat", pivot: HAT_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0.09, 0.13, 0], ease: "easeOut" },
            { t: 1.6, value: [0.09, 0.13, 0], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0], ease: "easeIn" },
            { t: 2.6, value: [0, 0, 0] },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0, 0, -0.55], ease: "easeOut" },
            { t: 1.6, value: [0, 0, -0.55], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0], ease: "easeIn" },
            { t: 2.6, value: [0, 0, 0] },
          ],
        },
        // The head bows into the tip and straightens back up.
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.7, value: [0.16, 0, 0.06], ease: "easeOut" },
            { t: 1.6, value: [0.16, 0, 0.06], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0], ease: "easeIn" },
            { t: 2.6, value: [0, 0, 0] },
          ],
        },
        // The body leans a touch into the bow.
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.7, value: [0.05, 0, 0], ease: "easeOut" },
            { t: 1.6, value: [0.05, 0, 0], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0], ease: "easeIn" },
            { t: 2.6, value: [0, 0, 0] },
          ],
        },
      ],
    },
  ],
});
